import { isLocal } from '@/utils/axleEnvironment';
import { graphQlPath } from '@/utils/axleUrls';
import { envVars } from '@/utils/envVars';
import { createHttpLink } from '@apollo/client';
import { createFragmentRegistry } from '@apollo/client/cache';
import type { InMemoryCacheConfig } from '@apollo/client/cache/inmemory/types';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import Cookies from 'js-cookie';

import { FieldPolicies } from './FieldPolicies';
import { commonFragments } from './fragments';

if (isLocal) {
    loadDevMessages();
    loadErrorMessages();
}

// TODO(jessica): Use BatchHttpLink once server support is in place.
const baseHeaders: Record<string, string> = {
    'X-Axle-Client': 'PARTNER_DASHBOARD',
};

/**
 * Custom fetch function that reads the CSRF token fresh from cookies on each request.
 * This ensures the X-CSRFTOKEN header is always up-to-date, similar to how axios handles
 * CSRF tokens with the withXSRFToken option.
 * The cookie name is environment-configurable (e.g., 'csrftoken_demo' in demo environment).
 */
const fetchWithCsrf: typeof fetch = (input, init) => {
    const csrfToken = Cookies.get(envVars.csrfCookieName);
    const headers = new Headers(init?.headers);

    // Set base headers
    Object.entries(baseHeaders).forEach(([key, value]) => {
        if (!headers.has(key)) {
            headers.set(key, value);
        }
    });

    // Set CSRF token if available
    if (csrfToken) {
        headers.set('X-CSRFTOKEN', csrfToken);
    }

    return fetch(input, {
        ...init,
        headers,
    });
};

export const APOLLO_LINK = createHttpLink({
    uri: graphQlPath,
    credentials: 'include',
    fetch: fetchWithCsrf,
});

export const APOLLO_CACHE_CONFIG: InMemoryCacheConfig = {
    /**
     * Register common fragments globally so they can be used in queries
     * without explicitly importing and including them.
     */
    fragments: createFragmentRegistry(commonFragments),
    /**
     * By defining which types are possible for a given interface or union type,
     * we can define shared type policies for the interface or union rather than
     * have to define them on each type individually.
     */
    possibleTypes: {
        RecurrenceRule: ['ShiftBlockRecurrence', 'ShiftRecurrence', 'RequestToCreateShiftBlock'],
        ScheduleEvent: ['ShiftBlockOccurrence', 'ShiftOccurrence', 'TentativeShiftBlockOccurrence'],
    },
    /*
     * Policies affecting how Apollo handles raw data coming in to the cache
     * and the cached data coming out of the cacheare defined here.
     *
     * Important: Queries called with a fetchPolicy of `no-cache` skip all of this
     * logic, which may produce unexpected behavior. Most cases where someone may
     * think to use 'no-cache' are better-served by 'network-only' instead, which ensures
     * that the query is always pulled fresh from the server when mounting, but also
     * passes the result through these transformations.
     *
     * See: https://www.apollographql.com/docs/react/caching/cache-field-behavior
     */
    typePolicies: {
        Clinician: {
            fields: {
                createdAt: FieldPolicies.datetime,
                updatedAt: FieldPolicies.datetime,
                permissions: {
                    merge: true,
                },
            },
        },
        ClinicianAppointment: {
            fields: {
                createdAt: FieldPolicies.datetime,
                startDatetime: FieldPolicies.datetime,
                endDatetime: FieldPolicies.datetime,
            },
        },
        ClinicianReservation: {
            fields: {
                startDatetime: FieldPolicies.datetime,
                endDatetime: FieldPolicies.datetime,
            },
        },
        ClinicianSchedule: {
            fields: {
                startDate: FieldPolicies.datetime,
                endDate: FieldPolicies.datetime,
                shifts: {
                    merge: false,
                },
                shiftBlocks: {
                    merge: false,
                },
                tentativeShiftBlocks: {
                    merge: false,
                },
                clinicianAppointments: {
                    merge: false,
                },
                clinicianReservations: {
                    merge: false,
                },
            },
        },
        ClinicianVisitPaginatedList: {
            merge: true,
        },
        Communication: {
            fields: {
                sentAt: FieldPolicies.datetime,
            },
        },
        DateFulfillmentWindow: {
            fields: {
                startDate: FieldPolicies.date,
                endDate: FieldPolicies.date,
            },
        },
        DecisionSupportQuery: {
            // This node is merely organizational, so we can safely merge it.
            merge: true,
        },
        DisciplinePaginatedList: {
            // TODO(jessica): Replace this with a proper paginated list merge policy once implemented and tested.
            merge: true,
        },
        Document: {
            fields: {
                createdAt: FieldPolicies.datetime,
                updatedAt: FieldPolicies.datetime,
            },
        },
        EpisodeOfCare: {
            fields: {
                startDate: FieldPolicies.date,
                endDate: FieldPolicies.date,
            },
        },
        ExternalId: {
            keyFields: false,
        },
        OptionalDateWindow: {
            merge: true,
            fields: {
                startDate: FieldPolicies.date,
                endDate: FieldPolicies.date,
            },
        },
        OptionalTimeWindow: {
            merge: true,
            fields: {
                startDatetime: FieldPolicies.datetime,
                endDatetime: FieldPolicies.datetime,
            },
        },
        Order: {
            fields: {
                activeOccurrences: {
                    merge: false,
                },
                createdAt: FieldPolicies.datetime,
                updatedAt: FieldPolicies.datetime,
                discontinuedAt: FieldPolicies.datetime,
                frequency: {
                    merge: true,
                },
                prn: {
                    merge: true,
                },
            },
        },
        OrderPaginatedList: {
            merge: true,
        },
        OrganizationPaginatedList: {
            merge: true,
        },
        Patient: {
            fields: {
                dateOfBirth: FieldPolicies.date,
                createdAt: FieldPolicies.datetime,
                permissions: {
                    merge: true,
                },
                // 'merge:false' here means to not merge and instead replace the existing list of
                // preferred timeslots with the incoming list
                preferredTimeslots: {
                    merge: false,
                },
                updatedAt: FieldPolicies.datetime,
            },
        },
        PatientPaginatedList: {
            merge: true,
        },
        Query: {
            fields: {
                confirmedAvailability: {
                    // 'merge:false' here means to not merge and instead replace the existing availability
                    // with the incoming availability. This is because availability can change between
                    // different queries with the same inputs over time, and the results are not themselves normalized.
                    merge: false,
                },
                scheduleChangeRequests: {
                    // We can safely merge this non-normalized object because it is merely organizational.
                    merge: true,
                },
                unconfirmedAvailability: {
                    // 'merge:false' here means to not merge and instead replace the existing availability
                    // with the incoming availability. This is because availability can change between
                    // different queries with the same inputs over time, and the results are not themselves normalized.
                    merge: false,
                },
                // 'merge:false' here means to replace the existing list of
                // viewable visit outcomes with the incoming list
                viewableVisitOutcomes: {
                    merge: false,
                },
                // 'merge:false' here means to replace the existing list of
                // viewable visit states with the incoming list
                viewableVisitStates: {
                    merge: false,
                },
            },
        },
        RecurrenceRule: {
            fields: {
                startDatetime: FieldPolicies.datetime,
                endDatetime: FieldPolicies.datetime,
                excludeDatetimes: FieldPolicies.datetime,
                untilDatetime: FieldPolicies.datetime,
                createdAt: FieldPolicies.datetime,
                updatedAt: FieldPolicies.datetime,
                deletedAt: FieldPolicies.datetime,
            },
        },
        Reservation: {
            fields: {
                startDatetime: FieldPolicies.datetime,
                expiryDatetime: FieldPolicies.datetime,
            },
        },
        ScheduleChangeRequests: {
            // We can safely merge this non-normalized object because it is merely organizational.
            merge: true,
        },
        ScheduleEvent: {
            fields: {
                createdAt: FieldPolicies.datetime,
                startDatetime: FieldPolicies.datetime,
                endDatetime: FieldPolicies.datetime,
            },
        },
        ShiftRecurrence: {
            fields: {
                mobileTerritories: {
                    merge: false,
                },
                virtualTerritories: {
                    merge: false,
                },
            },
        },
        ShiftRecurrenceQualificationRestriction: {
            fields: {
                startOffsetMinutes: Number,
                endOffsetMinutes: Number,
            },
        },
        ShiftRecurrenceServiceLimit: {
            keyFields: ['service', ['id']],
        },
        SyncRecord: {
            fields: {
                createdAt: FieldPolicies.datetime,
            },
        },
        TentativeRoutedVisit: {
            fields: {
                tentativeStartDatetime: FieldPolicies.datetime,
            },
        },
        TimeFulfillmentWindow: {
            merge: true,
            fields: {
                startDatetime: FieldPolicies.datetime,
                endDatetime: FieldPolicies.datetime,
            },
        },
        TimestampedCoordinates: {
            fields: {
                timestamp: FieldPolicies.datetime,
            },
        },
        TimeWindow: {
            fields: {
                start: FieldPolicies.datetime,
                end: FieldPolicies.datetime,
                startDatetime: FieldPolicies.datetime,
                endDatetime: FieldPolicies.datetime,
            },
        },
        Visit: {
            fields: {
                assignedClinicians: {
                    // 'merge:false' here means to not merge and instead replace the existing list of
                    // assigned clinicians with the incoming list
                    merge: false,
                },
                clinicianAppointments: {
                    // 'merge:false' here means to not merge and instead replace the existing list of
                    // appointments with the incoming list
                    merge: false,
                },
                createdAt: FieldPolicies.datetime,
                date: FieldPolicies.date,
                endDatetime: FieldPolicies.datetime,
                // This is a non-normalized object that is unique per visit, so we can safely merge it.
                contact: {
                    merge: true,
                },
                permissions: {
                    merge: true,
                },
                // 'merge:false' here means to replace the existing list of
                // preferred timeslots with the incoming list
                preferredTimeslots: {
                    merge: false,
                },
                startDatetime: FieldPolicies.datetime,
                tentativeDate: FieldPolicies.date,
                updatedAt: FieldPolicies.datetime,
            },
        },
        VisitishDateRecommendation: {
            fields: { date: FieldPolicies.date },
        },
        VisitOrder: {
            fields: {
                createdAt: FieldPolicies.datetime,
                fromDate: FieldPolicies.date,
                toDate: FieldPolicies.date,
                updatedAt: FieldPolicies.datetime,
            },
        },
        VisitRequest: {
            fields: {
                appointments: {
                    merge: false,
                },
                tentativeDate: FieldPolicies.date,
                warnings: {
                    merge: false,
                },
            },
        },
        VisitRequestAppointment: {
            fields: {
                // 'merge:false' here means to not merge and instead replace the existing list of
                // services with the incoming list
                services: {
                    merge: false,
                },
            },
        },
        VisitRequestDateRecommendation: {
            fields: {
                date: FieldPolicies.date,
            },
        },
        VisitStateChange: {
            fields: {
                createdAt: FieldPolicies.datetime,
                updatedAt: FieldPolicies.datetime,
            },
        },
        VisitTimeslotPreference: {
            fields: {
                startDatetime: FieldPolicies.datetime,
                endDatetime: FieldPolicies.datetime,
            },
        },
    },
};
